import React from "react";

// Texts and per-language defaults of the local business structured data
// generator. The English version is written for US search (target keyword:
// "local business schema generator"), not translated word for word.
// The generated JSON-LD keys and values follow schema.org in both languages.

const fr = {
  seo: {
    title:
      "Générateur de données structurées locales gratuit | JSON-LD LocalBusiness",
    description:
      "Générez gratuitement vos données structurées JSON-LD LocalBusiness pour le SEO local : remplissez le formulaire, copiez le code, collez-le sur votre site.",
    ogTitle: "Générateur de données structurées locales gratuit | Localranker",
    ogDescription:
      "Générez gratuitement vos données structurées JSON-LD LocalBusiness pour améliorer votre SEO local.",
  },
  hero: {
    title: "Générateur de données structurées locales",
    subtitle:
      "Optimisez votre SEO local avec des données structurées JSON-LD pour votre entreprise. Remplissez le formulaire et copiez le code généré.",
    cta: "Commencer",
  },
  steps: {
    badge: "Mode d'emploi",
    title: (
      <>
        Générez vos données structurées en <em>3 étapes</em>
      </>
    ),
    items: [
      {
        title: "Remplissez vos informations",
        desc: "Nom, URL, adresse, téléphone, horaires et réseaux sociaux",
      },
      {
        title: "Copiez le code généré",
        desc: "Collez le JSON-LD dans la balise <head> de votre site web",
      },
      {
        title: "Validez avec Google",
        desc: "Testez votre balisage avec le Rich Results Test de Google",
      },
    ],
  },
  days: {
    monday: "Lundi",
    tuesday: "Mardi",
    wednesday: "Mercredi",
    thursday: "Jeudi",
    friday: "Vendredi",
    saturday: "Samedi",
    sunday: "Dimanche",
  },
  countries: [
    { code: "FR", label: "France" },
    { code: "BE", label: "Belgique" },
    { code: "CH", label: "Suisse" },
    { code: "LU", label: "Luxembourg" },
    { code: "CA", label: "Canada" },
    { code: "US", label: "États-Unis" },
    { code: "DE", label: "Allemagne" },
    { code: "ES", label: "Espagne" },
    { code: "IT", label: "Italie" },
    { code: "GB", label: "Royaume-Uni" },
    { code: "PT", label: "Portugal" },
    { code: "NL", label: "Pays-Bas" },
    { code: "MA", label: "Maroc" },
    { code: "TN", label: "Tunisie" },
    { code: "SN", label: "Sénégal" },
    { code: "CI", label: "Côte d'Ivoire" },
  ],
  defaultCountry: "FR",
  example: {
    form: {
      name: "Boulangerie Dupont",
      url: "https://boulangerie-dupont.fr",
      images:
        "https://boulangerie-dupont.fr/images/facade.jpg, https://boulangerie-dupont.fr/images/logo.png",
      telephone: "+33 1 23 45 67 89",
      streetAddress: "15 Rue du Faubourg Saint-Honoré",
      addressLocality: "Paris",
      addressRegion: "Île-de-France",
      postalCode: "75008",
      addressCountry: "FR",
      facebook: "https://facebook.com/boulangerie.dupont",
      instagram: "https://instagram.com/boulangerie_dupont",
      youtube: "",
      x: "",
      tiktok: "",
      id: "https://boulangerie-dupont.fr/#localbusiness",
      priceRange: "€€",
      latitude: "48.8698",
      longitude: "2.3156",
      type: "Bakery",
    },
    closedDays: ["sunday"],
    slots: [
      { opens: "07:00", closes: "13:00" },
      { opens: "15:00", closes: "19:30" },
    ],
  },
  form: {
    mainTitle: "Informations principales",
    name: "Nom de l'entreprise *",
    namePlaceholder: "Nom",
    url: "URL du site *",
    images: "URL(s) image/logo (séparées par des virgules)",
    telephone: "Téléphone *",
    telephonePlaceholder: "+33...",
    addressTitle: "Adresse",
    street: "Rue *",
    streetPlaceholder: "12 Rue ...",
    city: "Ville *",
    cityPlaceholder: "Ville",
    region: "Région/Département",
    regionPlaceholder: "Région",
    postal: "Code postal *",
    postalPlaceholder: "75002",
    country: "Pays *",
    socialTitle: "Réseaux sociaux (optionnels)",
    socialHint: "N'ajoutez que les réseaux sociaux effectivement utilisés.",
    hoursTitle: "Horaires d'ouverture",
    closed: "Fermé",
    allDay: "24h/24",
    copyToAll: "Copier vers tous",
    resetDay: "Réinitialiser",
    addSlot: "+ Ajouter un créneau",
    hoursHint: "Vérifiez vos horaires après génération.",
    advancedTitle: "Options avancées",
    hide: "Masquer",
    show: "Afficher",
    idLabel: "@id (URL canonique)",
    idPlaceholder: "https://exemple.com/#localbusiness",
    priceRangePlaceholder: "€€",
    latitudePlaceholder: "48.8566",
    longitudePlaceholder: "2.3522",
    typeLabel: "Type (@type)",
    fillExample: "✨ Remplir un exemple",
    reset: "🔄 Réinitialiser",
  },
  preview: {
    title: "JSON-LD Généré",
    minify: "Minifier",
    copy: "Copier le code",
    copied: "✓ Copié !",
    download: "Télécharger .json",
    remaining: (n) => `Champs requis restants : ${n}`,
  },
  seoSections: (
    <>
      <article className="sd-seo__block">
        <h2>Qu'est-ce qu'un générateur de données structurées locales ?</h2>
        <p>
          Un <strong>générateur de données structurées locales</strong> est un outil qui crée automatiquement
          le balisage JSON-LD conforme au vocabulaire <strong>Schema.org</strong> pour votre entreprise locale.
          Ce code, une fois intégré à votre site, permet à Google de mieux comprendre les informations
          essentielles de votre établissement : nom, adresse, horaires, téléphone et bien plus.
        </p>
        <p>
          Les données structurées sont un standard du web sémantique. Elles transforment
          des informations lisibles par l'humain en un format que les moteurs de recherche
          peuvent exploiter directement, améliorant ainsi votre visibilité dans les résultats locaux.
        </p>
      </article>

      <article className="sd-seo__block">
        <h2>Pourquoi les données structurées sont essentielles pour le SEO local ?</h2>
        <p>
          Google utilise les données structurées pour enrichir ses résultats de recherche
          et mieux positionner les entreprises locales. Voici ce que vous gagnez concrètement :
        </p>
        <div className="sd-seo__benefits">
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">🎯</span>
            <div>
              <strong>Meilleur positionnement local</strong>
              <p>
                Google privilégie les sites avec des données structurées valides dans le Local Pack
                et Google Maps, augmentant votre visibilité face à la concurrence.
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">✨</span>
            <div>
              <strong>Rich snippets enrichis</strong>
              <p>
                Affichez vos horaires, votre note, votre adresse directement dans les résultats Google.
                Les rich snippets augmentent le taux de clic de 20 à 30%.
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">🤖</span>
            <div>
              <strong>Compréhension par les IA</strong>
              <p>
                Les assistants vocaux (Google Assistant, Siri) et les résultats IA s'appuient
                sur les données structurées pour répondre aux requêtes locales.
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">📊</span>
            <div>
              <strong>Knowledge Panel</strong>
              <p>
                Les données structurées alimentent le Knowledge Panel de votre entreprise,
                l'encart qui apparaît à droite des résultats Google avec toutes vos informations.
              </p>
            </div>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>Comment fonctionne le balisage JSON-LD LocalBusiness ?</h2>
        <p>
          Le format <strong>JSON-LD</strong> (JavaScript Object Notation for Linked Data) est le format
          recommandé par Google pour les données structurées. Contrairement aux microdonnées ou RDFa,
          il se place dans une balise <code>&lt;script&gt;</code> dans le <code>&lt;head&gt;</code> de votre page,
          sans modifier votre HTML visible.
        </p>
        <div className="sd-seo__steps-list">
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">1</span>
            <div>
              <strong>Renseignez vos informations</strong>
              <p>Nom de l'entreprise, URL, adresse postale complète, téléphone et horaires d'ouverture.</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">2</span>
            <div>
              <strong>Le code JSON-LD est généré en temps réel</strong>
              <p>Notre outil construit automatiquement la structure conforme à Schema.org avec le type LocalBusiness.</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">3</span>
            <div>
              <strong>Intégrez le code à votre site</strong>
              <p>Copiez le code et collez-le dans la balise &lt;head&gt; de votre page. Compatible WordPress, Shopify, Wix et tous les CMS.</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">4</span>
            <div>
              <strong>Validez avec l'outil de test Google</strong>
              <p>Utilisez le Rich Results Test pour vérifier que votre balisage est correctement détecté par Google.</p>
            </div>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>Quels types de données structurées pour le SEO local ?</h2>
        <p>
          Le vocabulaire Schema.org propose de nombreux sous-types de <strong>LocalBusiness</strong>,
          chacun adapté à un secteur d'activité. Utiliser le type le plus spécifique possible
          aide Google à mieux catégoriser votre entreprise :
        </p>
        <div className="sd-seo__types-grid">
          <div className="sd-seo__type-group">
            <h4>Restauration</h4>
            <ul>
              <li>Restaurant</li>
              <li>Bakery</li>
              <li>CafeOrCoffeeShop</li>
              <li>BarOrPub</li>
              <li>FastFoodRestaurant</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Commerce</h4>
            <ul>
              <li>Store</li>
              <li>ClothingStore</li>
              <li>ElectronicsStore</li>
              <li>GroceryStore</li>
              <li>JewelryStore</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Santé &amp; Beauté</h4>
            <ul>
              <li>Dentist</li>
              <li>MedicalClinic</li>
              <li>HairSalon</li>
              <li>BeautySalon</li>
              <li>Pharmacy</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Services</h4>
            <ul>
              <li>AutoRepair</li>
              <li>Electrician</li>
              <li>Plumber</li>
              <li>RealEstateAgent</li>
              <li>AccountingService</li>
            </ul>
          </div>
        </div>
        <p>
          Notre générateur propose plus de 50 types. Sélectionnez celui qui correspond
          le mieux à votre activité dans les options avancées du formulaire.
        </p>
      </article>

      <article className="sd-seo__block">
        <h2>Données structurées locales : les champs indispensables</h2>
        <p>
          Pour que Google exploite pleinement vos données structurées, certains champs
          sont particulièrement importants. Voici les priorités :
        </p>
        <div className="sd-seo__fields-grid">
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Requis</span>
            <h4>name</h4>
            <p>Le nom exact de votre établissement, tel qu'il apparaît sur votre fiche Google Business Profile.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Requis</span>
            <h4>address</h4>
            <p>Adresse postale complète incluant rue, ville, code postal et pays.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Requis</span>
            <h4>telephone</h4>
            <p>Numéro de téléphone au format international (+33 pour la France).</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommandé</span>
            <h4>openingHoursSpecification</h4>
            <p>Horaires d'ouverture détaillés pour chaque jour de la semaine.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommandé</span>
            <h4>geo</h4>
            <p>Coordonnées GPS (latitude/longitude) pour un positionnement précis sur la carte.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommandé</span>
            <h4>sameAs</h4>
            <p>Liens vers vos profils sur les réseaux sociaux pour renforcer votre entité.</p>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>Questions fréquentes sur les données structurées locales</h2>
        <div className="sd-seo__faq">
          <div className="sd-seo__faq-item">
            <h3>Les données structurées sont-elles obligatoires pour le SEO local ?</h3>
            <p>
              Non, elles ne sont pas obligatoires, mais elles sont <strong>fortement recommandées</strong> par Google.
              Les sites avec des données structurées valides ont significativement plus de chances
              d'apparaître dans les rich snippets et le Local Pack. C'est un avantage concurrentiel
              que la plupart des entreprises locales négligent encore.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Où placer le code JSON-LD sur mon site ?</h3>
            <p>
              Le code JSON-LD doit être placé dans la balise <code>&lt;head&gt;</code> de votre page d'accueil
              ou de votre page de contact. Sur WordPress, vous pouvez utiliser un plugin SEO comme Yoast
              ou Rank Math, ou coller le code directement dans le header via votre thème. Sur Shopify, Wix
              ou Squarespace, utilisez la zone de code personnalisé dans les paramètres.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Puis-je avoir plusieurs blocs JSON-LD sur une même page ?</h3>
            <p>
              Oui, vous pouvez avoir plusieurs balises <code>&lt;script type="application/ld+json"&gt;</code>
              sur une même page. C'est même recommandé si vous avez plusieurs types d'entités à décrire
              (par exemple un LocalBusiness et un FAQ). Google sait les interpréter séparément.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Comment vérifier que mes données structurées fonctionnent ?</h3>
            <p>
              Utilisez le <strong>Rich Results Test</strong> de Google (search.google.com/test/rich-results)
              pour valider votre balisage. Vous pouvez aussi consulter le rapport "Améliorations"
              dans la Google Search Console pour suivre la détection de vos données structurées au fil du temps.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Ce générateur est-il gratuit ?</h3>
            <p>
              Oui, notre générateur de données structurées locales est <strong>100% gratuit</strong> et sans limite d'utilisation.
              Vous pouvez générer autant de balisages JSON-LD que nécessaire pour tous vos établissements.
              Pour les réseaux multi-établissements, LocalRanker propose une solution de gestion à grande échelle.
            </p>
          </div>
        </div>
      </article>
    </>
  ),
};

const en = {
  seo: {
    title: "Free Local Business Schema Generator (JSON-LD) | Localranker",
    description:
      "Free local business schema generator: fill in a simple form to create LocalBusiness JSON-LD markup, then copy, paste it on your site and test it with Google.",
    ogTitle: "Free Local Business Schema Generator (JSON-LD) | Localranker",
    ogDescription:
      "Create LocalBusiness schema markup in JSON-LD for free. Copy, paste and test it with Google.",
  },
  hero: {
    title: "Local Business Schema Generator",
    subtitle:
      "Create LocalBusiness schema markup in JSON-LD for your business. Fill in the form, copy the code and paste it into your website.",
    cta: "Get started",
  },
  steps: {
    badge: "How to use it",
    title: (
      <>
        Generate your local business schema in <em>3 steps</em>
      </>
    ),
    items: [
      {
        title: "Enter your business details",
        desc: "Name, URL, address, phone number, opening hours and social profiles",
      },
      {
        title: "Copy the generated code",
        desc: "Paste the JSON-LD into the <head> of your website",
      },
      {
        title: "Test it with Google",
        desc: "Check your markup with Google's Rich Results Test",
      },
    ],
  },
  days: {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  },
  countries: [
    { code: "US", label: "United States" },
    { code: "CA", label: "Canada" },
    { code: "GB", label: "United Kingdom" },
    { code: "FR", label: "France" },
    { code: "BE", label: "Belgium" },
    { code: "CH", label: "Switzerland" },
    { code: "LU", label: "Luxembourg" },
    { code: "DE", label: "Germany" },
    { code: "ES", label: "Spain" },
    { code: "IT", label: "Italy" },
    { code: "PT", label: "Portugal" },
    { code: "NL", label: "Netherlands" },
    { code: "MA", label: "Morocco" },
    { code: "TN", label: "Tunisia" },
    { code: "SN", label: "Senegal" },
    { code: "CI", label: "Ivory Coast" },
  ],
  defaultCountry: "US",
  // Fictional business: 555-01xx phone numbers are reserved for fiction and
  // .example is a reserved domain.
  example: {
    form: {
      name: "Maple Street Bakery",
      url: "https://www.maplestreetbakery.example",
      images:
        "https://www.maplestreetbakery.example/images/storefront.jpg, https://www.maplestreetbakery.example/images/logo.png",
      telephone: "+1 512-555-0142",
      streetAddress: "1200 South Congress Ave",
      addressLocality: "Austin",
      addressRegion: "TX",
      postalCode: "78704",
      addressCountry: "US",
      facebook: "https://facebook.com/maplestreetbakery.example",
      instagram: "https://instagram.com/maplestreetbakery.example",
      youtube: "",
      x: "",
      tiktok: "",
      id: "https://www.maplestreetbakery.example/#localbusiness",
      priceRange: "$$",
      latitude: "30.2515",
      longitude: "-97.7495",
      type: "Bakery",
    },
    closedDays: ["sunday"],
    slots: [{ opens: "07:00", closes: "18:00" }],
  },
  form: {
    mainTitle: "Business details",
    name: "Business name *",
    namePlaceholder: "Business name",
    url: "Website URL *",
    images: "Image/logo URL(s) (comma-separated)",
    telephone: "Phone number *",
    telephonePlaceholder: "+1 555-555-0100",
    addressTitle: "Address",
    street: "Street address *",
    streetPlaceholder: "123 Main St",
    city: "City *",
    cityPlaceholder: "City",
    region: "State",
    regionPlaceholder: "TX",
    postal: "ZIP code *",
    postalPlaceholder: "78701",
    country: "Country *",
    socialTitle: "Social profiles (optional)",
    socialHint: "Only add the social profiles you actually use.",
    hoursTitle: "Opening hours",
    closed: "Closed",
    allDay: "Open 24 hours",
    copyToAll: "Copy to all days",
    resetDay: "Reset",
    addSlot: "+ Add time slot",
    hoursHint: "Double-check your hours in the generated code.",
    advancedTitle: "Advanced options",
    hide: "Hide",
    show: "Show",
    idLabel: "@id (canonical URL)",
    idPlaceholder: "https://example.com/#localbusiness",
    priceRangePlaceholder: "$$",
    latitudePlaceholder: "30.2672",
    longitudePlaceholder: "-97.7431",
    typeLabel: "Type (@type)",
    fillExample: "✨ Fill in an example",
    reset: "🔄 Reset",
  },
  preview: {
    title: "Generated JSON-LD",
    minify: "Minify",
    copy: "Copy code",
    copied: "✓ Copied!",
    download: "Download .json",
    remaining: (n) =>
      `${n} required field${n > 1 ? "s" : ""} left`,
  },
  seoSections: (
    <>
      <article className="sd-seo__block">
        <h2>What is local business schema?</h2>
        <p>
          <strong>Local business schema</strong> is a type of schema markup, built on the{" "}
          <strong>Schema.org</strong> <code>LocalBusiness</code> type, that describes a business with a
          physical location: its name, address, phone number, opening hours, website and social profiles.
          You add it to your site as a small block of JSON-LD code. Visitors don't see it, but search
          engines read it.
        </p>
        <p>
          A <strong>local business schema generator</strong> writes that code for you. Fill in the form
          above and the tool builds valid JSON-LD in real time. Want to see a complete local business
          schema example? Click "Fill in an example": the code on the right is a full LocalBusiness
          markup for a fictional bakery in Austin, Texas.
        </p>
      </article>

      <article className="sd-seo__block">
        <h2>Why local business schema matters for local SEO</h2>
        <p>
          Schema markup doesn't replace your Google Business Profile or good content, but it gives
          search engines your key business details in a format they don't have to guess. Here is
          what it does for you:
        </p>
        <div className="sd-seo__benefits">
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">🎯</span>
            <div>
              <strong>Clear, consistent business information</strong>
              <p>
                Your name, address and phone number are stated explicitly, which helps Google match
                your website with your Google Business Profile and your local listings.
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">✨</span>
            <div>
              <strong>Eligible for richer results</strong>
              <p>
                Structured data makes it easier for Google to show details such as your hours,
                address or price range when your business appears in search results.
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">🤖</span>
            <div>
              <strong>Easier for AI assistants to understand</strong>
              <p>
                Voice assistants and AI-powered search can read structured data to answer local
                questions like "is it open now?" or "where is it located?".
              </p>
            </div>
          </div>
          <div className="sd-seo__benefit">
            <span className="sd-seo__benefit-icon">📊</span>
            <div>
              <strong>Stronger business entity</strong>
              <p>
                Linking your website, social profiles and location in one place helps search engines
                connect the information about your business, including the knowledge panel.
              </p>
            </div>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>How LocalBusiness JSON-LD schema markup works</h2>
        <p>
          <strong>JSON-LD</strong> (JavaScript Object Notation for Linked Data) is the format Google
          recommends for structured data. Unlike Microdata or RDFa, it goes in a{" "}
          <code>&lt;script&gt;</code> tag in the <code>&lt;head&gt;</code> of your page, without
          changing your visible HTML.
        </p>
        <div className="sd-seo__steps-list">
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">1</span>
            <div>
              <strong>Enter your business details</strong>
              <p>Business name, URL, full street address, phone number and opening hours.</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">2</span>
            <div>
              <strong>The JSON-LD is generated in real time</strong>
              <p>The tool builds Schema.org-compliant markup with the LocalBusiness type (or the more specific type you pick).</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">3</span>
            <div>
              <strong>Add the code to your site</strong>
              <p>Copy the code and paste it into the &lt;head&gt; of your page. Works with WordPress, Shopify, Wix, Squarespace and any CMS.</p>
            </div>
          </div>
          <div className="sd-seo__step">
            <span className="sd-seo__step-num">4</span>
            <div>
              <strong>Test it with Google</strong>
              <p>Run the Rich Results Test to check that Google detects your markup correctly.</p>
            </div>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>Which LocalBusiness type should you use?</h2>
        <p>
          Schema.org offers many subtypes of <strong>LocalBusiness</strong>, each one suited to a
          specific industry. Using the most specific type that fits helps Google categorize your
          business:
        </p>
        <div className="sd-seo__types-grid">
          <div className="sd-seo__type-group">
            <h4>Food &amp; Drink</h4>
            <ul>
              <li>Restaurant</li>
              <li>Bakery</li>
              <li>CafeOrCoffeeShop</li>
              <li>BarOrPub</li>
              <li>FastFoodRestaurant</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Retail</h4>
            <ul>
              <li>Store</li>
              <li>ClothingStore</li>
              <li>ElectronicsStore</li>
              <li>GroceryStore</li>
              <li>JewelryStore</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Health &amp; Beauty</h4>
            <ul>
              <li>Dentist</li>
              <li>MedicalClinic</li>
              <li>HairSalon</li>
              <li>BeautySalon</li>
              <li>Pharmacy</li>
            </ul>
          </div>
          <div className="sd-seo__type-group">
            <h4>Services</h4>
            <ul>
              <li>AutoRepair</li>
              <li>Electrician</li>
              <li>Plumber</li>
              <li>RealEstateAgent</li>
              <li>AccountingService</li>
            </ul>
          </div>
        </div>
        <p>
          The generator offers more than 50 types. Pick the one that best matches your business in
          the advanced options of the form.
        </p>
      </article>

      <article className="sd-seo__block">
        <h2>Key properties in local business schema markup</h2>
        <p>
          For Google to make the most of your structured data, some properties matter more than
          others. Here are the priorities:
        </p>
        <div className="sd-seo__fields-grid">
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Required</span>
            <h4>name</h4>
            <p>The exact name of your business, as it appears on your Google Business Profile.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Required</span>
            <h4>address</h4>
            <p>Full postal address: street, city, state, ZIP code and country.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--required">
            <span className="sd-seo__field-priority">Required</span>
            <h4>telephone</h4>
            <p>Phone number in international format (+1 for the US and Canada).</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommended</span>
            <h4>openingHoursSpecification</h4>
            <p>Detailed opening hours for each day of the week.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommended</span>
            <h4>geo</h4>
            <p>GPS coordinates (latitude/longitude) to pinpoint your location on the map.</p>
          </div>
          <div className="sd-seo__field-card sd-seo__field-card--recommended">
            <span className="sd-seo__field-priority">Recommended</span>
            <h4>sameAs</h4>
            <p>Links to your social profiles to strengthen your business entity.</p>
          </div>
        </div>
      </article>

      <article className="sd-seo__block">
        <h2>Local business schema FAQ</h2>
        <div className="sd-seo__faq">
          <div className="sd-seo__faq-item">
            <h3>Is local business schema required for local SEO?</h3>
            <p>
              No, it isn't required, but Google <strong>recommends</strong> structured data to help it
              understand your pages. It's a quick win that many local businesses still skip.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Where do I put the JSON-LD code on my site?</h3>
            <p>
              Place the JSON-LD in the <code>&lt;head&gt;</code> of your homepage or your contact page.
              On WordPress, you can use an SEO plugin like Yoast or Rank Math, or paste the code into
              your theme's header. On Shopify, Wix or Squarespace, use the custom code area in your
              site settings.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Can I have more than one JSON-LD block on a page?</h3>
            <p>
              Yes, a page can have several <code>&lt;script type="application/ld+json"&gt;</code> tags.
              That's the usual approach when you describe several kinds of entities (for example a
              LocalBusiness and an FAQ). Google reads them separately.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>How do I test my schema markup?</h3>
            <p>
              Use Google's <strong>Rich Results Test</strong> (search.google.com/test/rich-results) to
              validate your markup. You can also check the "Enhancements" reports in Google Search
              Console to track how your structured data is detected over time.
            </p>
          </div>
          <div className="sd-seo__faq-item">
            <h3>Is this schema markup generator free?</h3>
            <p>
              Yes, this local business schema generator is <strong>100% free</strong> with no usage
              limit. Generate as much JSON-LD as you need for all your locations. For multi-location
              brands, Localranker offers a platform to manage local SEO at scale.
            </p>
          </div>
        </div>
      </article>
    </>
  ),
};

const content = { fr, en };

export default content;
